import { Fragment, type ReactNode } from "react";

/**
 * Renders agent text safely as React nodes (never as HTML): paragraphs and line breaks, "- " / "• " bullets,
 * **bold**, `code`, [label](https://… | mailto:… | tel:…) and bare http(s) URLs (shown with their hostname).
 * No raw HTML and no images (remote images could leak the user's IP and read state). Anything else stays plain text.
 */
export function RichText({ text, className }: { text: string; className?: string }) {
  const blocks = text.replace(/\r\n?/g, "\n").split(/\n{2,}/);
  return (
    <div className={className}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} first={i === 0} />
      ))}
    </div>
  );
}

/** French typography on screen: a no-break space keeps « 18 h », « 7 h 30 » and « ? ! : ; » with what comes before. */
function typography(text: string): string {
  return text
    .replace(/(\d) h(?=$|[\s.,;:!?)»])/gu, "$1\u00a0h")
    .replace(/(\d\u00a0h) (\d{2})\b/gu, "$1\u00a0$2")
    .replace(/(\S) ([?!:;»])(?=$|\s)/gu, "$1\u00a0$2")
    .replace(/« /gu, "«\u00a0");
}

/**
 * Typography of one plain-text segment (never a `code` span nor a link target: a copied command keeps its spaces).
 * `before`: the character shown just before it (end of a code span, a link…), so « `ls` : » still gets its space.
 */
function typeset(segment: string, before: string): string {
  return typography(before + segment).slice(before.length);
}

function Block({ block, first }: { block: string; first: boolean }) {
  const lines = block.split("\n");
  const bullet = /^\s*(?:[-*•]|\d+[.)])\s+/;
  const isList = lines.length > 0 && lines.every((l) => bullet.test(l) || l.trim() === "");
  if (isList) {
    return (
      <ul className={first ? "list-disc space-y-0.5 pl-5" : "mt-2 list-disc space-y-0.5 pl-5"}>
        {lines
          .filter((l) => l.trim() !== "")
          .map((l, i) => (
            <li key={i}>{inline(l.replace(bullet, ""))}</li>
          ))}
      </ul>
    );
  }
  return (
    <p className={first ? "" : "mt-2"}>
      {lines.map((line, i) => {
        const heading = /^#{1,6}\s+(.*)$/.exec(line);
        return (
          <Fragment key={i}>
            {i > 0 ? <br /> : null}
            {heading ? <strong>{inline(heading[1] ?? "")}</strong> : inline(line)}
          </Fragment>
        );
      })}
    </p>
  );
}

// Markdown links accept http(s), mailto and tel targets; bare URLs only http(s). Everything else stays plain text.
const TOKEN =
  /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]{1,200}\]\((?:https?:\/\/|mailto:|tel:)[^\s)]+\)|https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"»])/g;

/** Only http(s), mailto and tel links are ever rendered (no javascript:, data:, relative or protocol-less URLs). */
export function safeLink(url: string): { href: string; host: string; external: boolean } | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol === "https:" || u.protocol === "http:") {
    if (!u.hostname || u.username || u.password) return null;
    return { href: u.href, host: u.hostname.replace(/^www\./, ""), external: true };
  }
  if (u.protocol === "mailto:" || u.protocol === "tel:") {
    const target = decodeURIComponent(u.pathname).trim();
    if (!target || /[\s<>]/.test(target)) return null;
    return { href: `${u.protocol}${encodeURIComponent(target).replace(/%40/g, "@").replace(/%2B/g, "+")}`, host: target, external: false };
  }
  return null;
}

/** A link whose destination is always visible: the hostname (or address) is shown next to the label. */
export function SafeLink({ url, label, className }: { url: string; label?: string; className?: string }) {
  const safe = safeLink(url);
  const text = label ?? url;
  if (!safe) return <>{text}</>;
  const showHost = label !== undefined && label.trim() !== safe.host;
  return (
    <a
      href={safe.href}
      {...(safe.external ? { target: "_blank", rel: "noopener noreferrer nofollow" } : {})}
      referrerPolicy="no-referrer"
      className={className ?? "underline underline-offset-2 wrap-anywhere"}
    >
      {safe.external && label === undefined ? safe.host + shortPath(url) : text}
      {showHost ? <span className="text-[0.85em] opacity-70"> ({safe.host})</span> : null}
    </a>
  );
}

function shortPath(url: string): string {
  try {
    const u = new URL(url);
    const rest = u.pathname === "/" ? "" : u.pathname;
    return rest.length > 40 ? `${rest.slice(0, 38)}…` : rest;
  } catch {
    return "";
  }
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(TOKEN)) {
    const token = match[0];
    const start = match.index ?? 0;
    if (start > last) out.push(typeset(text.slice(last, start), text.slice(Math.max(0, last - 1), last)));
    if (token.startsWith("**")) {
      out.push(<strong key={key++}>{typeset(token.slice(2, -2), text.slice(Math.max(0, start - 1), start))}</strong>);
    } else if (token.startsWith("`")) {
      out.push(
        <code key={key++} className="rounded bg-black/5 px-1 font-mono text-[0.92em] dark:bg-white/10">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("[")) {
      const m = /^\[([^\]]+)\]\((.+)\)$/.exec(token);
      out.push(m ? <SafeLink key={key++} url={m[2] ?? ""} label={typeset(m[1] ?? "", "")} /> : token);
    } else {
      out.push(<SafeLink key={key++} url={token} />);
    }
    last = start + token.length;
  }
  if (last < text.length) out.push(typeset(text.slice(last), text.slice(Math.max(0, last - 1), last)));
  return out;
}
